import cn from 'classnames';
import { useRouter } from 'next/router';
import { FormEvent, useRef, useState } from 'react';

import { useOnMount } from 'src/hooks/useOnMount';

import CloseXIcon from 'assets/icons/close-x.svg';
import SearchIcon from 'assets/icons/search.svg';

import SearchBar from 'components/Core/CoreSearch/components/SearchBar/index';

import styles from './NavSearch.module.scss';

export type NavSearchProps = {
  searchTarget: string;
  termInputPlaceholderLabel: string;
  searchInputLabel: string;
};

const NavSearch = ({
  searchTarget,
  termInputPlaceholderLabel,
  searchInputLabel,
}: NavSearchProps): JSX.Element => {
  const formRef = useRef<HTMLFormElement>(null);
  const searchBarRef = useRef<HTMLDivElement>(null);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const router = useRouter();

  const handleNavButton = () => {
    showSearchBar ? hideSearch() : openSearch();
  };

  const openSearch = () => {
    setShowSearchBar(true);
    setTimeout(() => {
      if (formRef.current) {
        const input = formRef.current.querySelector(`[name=term]`) as HTMLInputElement;
        input.focus();
      }
    }, 50);
  };

  const hideSearch = () => {
    setShowSearchBar(false);
  };

  const handleSearch = (e?: FormEvent) => {
    e?.preventDefault();
    if (formRef.current) {
      const formData = new FormData(formRef.current);
      const term = formData.get('term');
      const target = `${searchTarget}?term=${term}`;
      router.push(target);
      setShowSearchBar(false);
      handleClear();
    }
  };

  const handleClear = () => {
    if (formRef.current) {
      const input = formRef.current.querySelector(`[name=term]`) as HTMLInputElement;
      input.value = '';
      input.focus();
    }
  };

  const handleOutsideClick = (e: MouseEvent) => {
    const { target } = e;
    if (!target || !searchBarRef.current) {
      return;
    }
    if (!searchBarRef.current.contains(target as Node)) {
      hideSearch();
    }
  };

  useOnMount(
    () => {
      window.addEventListener('click', handleOutsideClick);
    },
    () => {
      window.removeEventListener('click', handleOutsideClick);
    }
  );

  const buttonAria = showSearchBar ? 'Close the search form.' : 'Open the search form.';

  return (
    <div ref={searchBarRef}>
      <button className={styles.searchButton} onClick={handleNavButton} aria-label={buttonAria}>
        {showSearchBar ? (
          <CloseXIcon className={styles.closeIcon}></CloseXIcon>
        ) : (
          <SearchIcon className={styles.searchIcon}></SearchIcon>
        )}
      </button>
      <search className={cn(styles.searchWrapper, showSearchBar && styles.showSearchBar)}>
        <div className={cn('container', 'container--true-1440')}>
          <SearchBar
            onSubmit={handleSearch}
            onClear={handleClear}
            ref={formRef}
            className={styles.searchBar}
            labels={{ termInputPlaceholderLabel, searchInputLabel }}
          ></SearchBar>
        </div>
      </search>
    </div>
  );
};

export default NavSearch;
